import { Box, Button, Switch, Typography, TextField } from "@mui/material";
import { images } from "../../../../Assets/assetsHelper";
import "./AccountsDetails.css";
import React, { useEffect, useState } from "react";
import { BarLoader } from "react-spinners";
import DeleteForm from "../../../../Components/Molecules/DeleteForm/DeleteForm";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  accountsURL,
  BaseURL,
  paymentURL,
  registrationURL,
} from "../../../../Hooks/URL";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import LoginIcon from "@mui/icons-material/Login";
import { PulseLoader } from "react-spinners";
import PrimaryButton from "../../../../Components/Atoms/Buttons/Primary/PrimaryButton";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import useSave from "../../../../Hooks/useSave";
import FormModal from "../../../../Components/Molecules/FormModal/FormModal";
import useFetch from "../../../../Hooks/useFetch";
import { toast } from "react-toastify";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import config from "../../../../config";

const ConfirmPayment = ({ handleClosePaymentModal, account }) => {
  const navigate = useNavigate();
  const [goSave, setGoSave] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [notes, setNotes] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [currencyId, setCurrencyId] = useState("");
  const { data, loading, error } = useFetch(
    `${paymentURL}/${account?.account?.id}/manualPayment`
  );
  const {
    saveState,
    loading: paymentLoading,
    error: paymentError,
    newData,
  } = useSave({
    url: `${paymentURL}/${account?.account?.id}/manualPayment`,
    goSave,
    setGoSave,
    newObject: notes
      ? {
          type: paymentMethod,
          note: notes,
          amountCents: paymentAmount ? parseInt(paymentAmount * 100) : null,
          currencyId: currencyId,
        }
      : {
          type: paymentMethod,
          amountCents: paymentAmount ? parseInt(paymentAmount * 100) : null,
          currencyId: currencyId,
        },
  });

  useEffect(() => {
    if (error?.status == "fail") {
      handleClosePaymentModal();
    }
  }, [error]);

  useEffect(() => {
    if (saveState.dataRes.status == "success") {
      handleClosePaymentModal();
      window.location.reload();
    }
  }, [saveState]);

  useEffect(() => {
    if (data) {
      setPaymentData(data?.data);
      setPaymentAmount(formatAmount(data?.data?.amountCents));
      setCurrencyId(data?.data?.currency?.id);
    }
  }, [data]);

  const handlePayment = () => {
    if (paymentAmount < 1) {
      toast.error("Payment amount must be greater than 0");
      return;
    }
    setGoSave(true);
  };

  const formatAmount = (amountCents) => {
    return (amountCents / 100).toFixed(2);
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    // Only allow numbers and decimal point
    if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
      setPaymentAmount(value);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {loading ? (
        <div className="loadingBox">
          <PulseLoader color={"var(--primary)"} />
        </div>
      ) : (
        <>
          <Typography
            variant="body1"
            sx={{
              mt: 0,
              textAlign: "center",
              color: "text.secondary",
            }}
          >
            Payment Summary
          </Typography>

          <Box sx={{ mt: 3, mb: 4 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 0,
                pt: 2,
                borderTop: "2px solid #e0e0e0",
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Total Amount:
              </Typography>
              <Typography
                variant="h6"
                fontWeight="bold"
                color={"var(--primary)"}
              >
                {formatAmount(paymentData?.amountCents)}{" "}
                {paymentData?.currencyCode}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Payment Amount:
            </Typography>
            <TextField
              fullWidth
              type="text"
              value={paymentAmount}
              onChange={handleAmountChange}
              placeholder="Enter payment amount"
              InputProps={{
                endAdornment: (
                  <Typography>{paymentData?.currencyCode}</Typography>
                ),
              }}
              sx={{ mb: 2 }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Payment Method:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              {[
                "cash",
                "vodafoneCash",
                "instaPay",
                "bankTransfer",
                "paymobLink",
                "exception",
                "other",
              ].map((method) => (
                <Button
                  variant={paymentMethod === method ? "contained" : "outlined"}
                  onClick={() => setPaymentMethod(method)}
                  sx={{
                    backgroundColor:
                      paymentMethod === method
                        ? "var(--primary)"
                        : "transparent",
                    color:
                      paymentMethod === method ? "white" : "var(--primary)",
                    borderColor: "var(--primary)",
                    fontSize: "0.7rem",
                    "&:hover": {
                      backgroundColor:
                        paymentMethod === method
                          ? "var(--primary)"
                          : "rgba(0, 0, 0, 0.04)",
                      borderColor: "var(--primary)",
                    },
                  }}
                >
                  {method}
                </Button>
              ))}
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Notes:
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes here..."
              variant="outlined"
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              mt: 2,
            }}
          >
            <Button
              onClick={handleClosePaymentModal}
              variant="outlined"
              sx={{
                minWidth: "120px",
                borderColor: "var(--primary)",
                color: "var(--primary)",
                "&:hover": {
                  borderColor: "var(--primary)",
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePayment}
              variant="contained"
              disabled={paymentLoading}
              sx={{
                minWidth: "120px",
                backgroundColor: "var(--primary)",
                color: "white",
                "&:hover": {
                  backgroundColor: "var(--primary)",
                  opacity: 0.9,
                },
              }}
            >
              {paymentLoading ? <PulseLoader size={8} color="white" /> : "Pay"}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

const DiscountModal = ({ handleCloseDiscountModal, account }) => {
  const [goSave, setGoSave] = useState(false);
  const [discountPercentage, setDiscountPercentage] = useState("");
  const { saveState, loading } = useSave({
    url: `${paymentURL}/${account?.account?.id}/discount`,
    goSave,
    setGoSave,
    newObject: {
      discountPercentage:
        parseInt(discountPercentage) == 0 ? null : parseInt(discountPercentage),
    },
  });

  useEffect(() => {
    if (saveState.dataRes.status === "success") {
      handleCloseDiscountModal();
      window.location.reload();
    }
  }, [saveState]);

  const handleDiscountChange = (e) => {
    const value = e.target.value;
    // Only allow numbers between 0 and 100
    if (value === "" || (Number(value) >= 0 && Number(value) <= 100)) {
      setDiscountPercentage(value);
    }
  };

  const handleApplyDiscount = () => {
    if (
      discountPercentage &&
      !isNaN(discountPercentage) &&
      parseInt(discountPercentage) >= 0 &&
      parseInt(discountPercentage) <= 100
    ) {
      setGoSave(true);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mt: 3, mb: 4 }}>
        <TextField
          fullWidth
          label="Discount Percentage"
          type="number"
          value={discountPercentage}
          onChange={handleDiscountChange}
          InputProps={{
            endAdornment: <Typography>%</Typography>,
            inputProps: {
              min: 0,
              max: 100,
              step: 1,
            },
          }}
          error={
            discountPercentage &&
            (Number(discountPercentage) < 0 || Number(discountPercentage) > 100)
          }
          helperText={"Discount must be between 0 and 100"}
          sx={{ mb: 2 }}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 2,
          mt: 2,
        }}
      >
        <Button
          onClick={handleCloseDiscountModal}
          variant="outlined"
          sx={{
            minWidth: "120px",
            borderColor: "var(--primary)",
            color: "var(--primary)",
            "&:hover": {
              borderColor: "var(--primary)",
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleApplyDiscount}
          variant="contained"
          disabled={loading}
          sx={{
            minWidth: "120px",
            backgroundColor: "var(--primary)",
            color: "white",
            "&:hover": {
              backgroundColor: "var(--primary)",
              opacity: 0.9,
            },
          }}
        >
          {loading ? <PulseLoader size={8} color="white" /> : "Apply Discount"}
        </Button>
      </Box>
    </Box>
  );
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    padding: 8,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: 8,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function AccountsDetails({ account, loading, setGo }) {
  const navigate = useNavigate();
  const [value, setValue] = useState(0);
  const [canEdit, setCanEdit] = useState(false);
  const [canPay, setCanPay] = useState(false);
  const [goSave, setGoSave] = useState(false);
  const [goSavePay, setGoSavePay] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);

  useEffect(() => {
    setCanEdit(account?.account?.canEdit || false);
    setCanPay(account?.account?.canPay || false);
  }, [account]);

  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [openDiscountModal, setOpenDiscountModal] = useState(false);

  const handleOpenPaymentModal = () => {
    setOpenPaymentModal(true);
  };

  const handleClosePaymentModal = () => {
    setOpenPaymentModal(false);
  };

  const handleOpenDiscountModal = () => {
    setOpenDiscountModal(true);
  };

  const handleCloseDiscountModal = () => {
    setOpenDiscountModal(false);
  };

  const { saveState, loading: saveLoading } = useSave({
    url: `${accountsURL}/${account?.account?.id}/actionsAbility`,
    goSave,
    setGoSave,
    newObject: { canEdit: !canEdit },
  });

  const { saveState: paySaveState, loading: paySaveLoading } = useSave({
    url: `${accountsURL}/${account?.account?.id}/actionsAbility`,
    goSave: goSavePay,
    setGoSave: setGoSavePay,
    newObject: { canPay: !canPay },
  });

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleCanEditToggle = () => {
    setGoSave(true);
  };

  const handleCanPayToggle = () => {
    setGoSavePay(true);
  };

  // Update canEdit state when save is successful or fails
  useEffect(() => {
    if (saveState.dataRes.status == "success" && !saveState.loadingRes) {
      setCanEdit(!canEdit);
    }
  }, [saveState]);

  // Update canPay state when save is successful or fails
  useEffect(() => {
    if (paySaveState.dataRes.status == "success" && !paySaveState.loadingRes) {
      setCanPay(!canPay);
    }
  }, [paySaveState]);

  const handleLoginAsAccount = async () => {
    if (!account?.account?.id) {
      toast.error("Account ID is missing");
      return;
    }

    setLoginLoading(true);
    try {
      const response = await axios.post(
        `${BaseURL}/admin/dashboard/accounts/${account?.account?.id}/login`,
        {},
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (response.data.status === "success") {
        toast.success(response.data.message || "Logged in successfully", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnFocusLoss: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });

        setTimeout(() => {
          window.open(config.REG_LINK, "_blank", "noopener,noreferrer");
        }, 1000);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to login as account",
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnFocusLoss: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        }
      );
    } finally {
      setLoginLoading(false);
    }
  };

  return (
    <div className="mainContainer">
      {account && !loading ? (
        <div className="TeacherCont">
          <Box>
            <h2 style={{ fontSize: "30px", margin: "0 0 10px 0" }}>
              Account Code: {account?.account?.code} - {account?.account?.type}
            </h2>
            <Tabs
              className="tabs"
              value={value}
              onChange={handleChange}
              aria-label="basic tabs example"
            >
              <Tab label="Account Details" />
              <Tab label="Teams" />
              <Tab label="Payment Info" />
            </Tabs>
            <CustomTabPanel value={value} index={0}>
              <Box
                sx={{
                  display: "flex",
                  marginBottom: "20px",
                }}
              >
                <PrimaryButton
                  classBtn={"center editButton"}
                  text={
                    <>
                      <LoginIcon
                        style={{
                          marginRight: "5px",
                          fontSize: "20px",
                          marginBottom: "3px",
                        }}
                      />
                      {"Login as this account"}
                    </>
                  }
                  style={{
                    textTransform: "capitalize",
                    borderRadius: "5px",
                    color: "var(--text-secondary)",
                    backgroundColor: "var(--text-primary)",
                    height: "40px",
                    padding: "0 30px",
                    margin: "0 20px 0 0",
                  }}
                  click={handleLoginAsAccount}
                  loading={loginLoading}
                  disabled={loginLoading}
                />
                {/* <DeleteForm
                        setGo={setGo}
                        title={"Are you sure to delete this Account ?"}
                      /> */}
              </Box>
              <div className="greyBox">
                <div style={{ width: "100%" }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      width: "100%",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ width: { xs: "100%", md: "50%" } }}>
                      <h3 style={{ fontSize: "20px", margin: "0 0 15px" }}>
                        Admin Details:
                      </h3>
                      <ul className="confirmList">
                        <li>
                          <span className="listTitle">Name:</span>{" "}
                          {account?.admin?.name}
                        </li>
                        <li>
                          <span className="listTitle">Email:</span>{" "}
                          {account?.admin?.email}
                        </li>
                        <li>
                          <span className="listTitle">Phone Number:</span>{" "}
                          {account?.admin?.contactNumber}
                        </li>
                        <li>
                          <span className="listTitle">Promo Code:</span>{" "}
                          {account?.account?.promoCode ?? "-"}
                        </li>
                        <li>
                          <span className="listTitle">Come From:</span>{" "}
                          {account?.account?.referralSource}
                        </li>
                        <li>
                          <span className="listTitle">Can Edit:</span>{" "}
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              ml: 1,
                            }}
                          >
                            {saveLoading ? (
                              <PulseLoader size={8} color="var(--primary)" />
                            ) : (
                              <>
                                {canEdit ? "Yes" : "No"}
                                <Switch
                                  checked={canEdit}
                                  onChange={handleCanEditToggle}
                                  color="primary"
                                  size="small"
                                  sx={{ ml: 1 }}
                                />
                              </>
                            )}
                          </Box>
                        </li>
                        <li>
                          <span className="listTitle">Can Pay:</span>{" "}
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              ml: 1,
                            }}
                          >
                            {paySaveLoading ? (
                              <PulseLoader size={8} color="var(--primary)" />
                            ) : (
                              <>
                                {canPay ? "Yes" : "No"}
                                <Switch
                                  checked={canPay}
                                  onChange={handleCanPayToggle}
                                  color="primary"
                                  size="small"
                                  sx={{ ml: 1 }}
                                />
                              </>
                            )}
                          </Box>
                        </li>
                        {account?.personalAccountInfo ? (
                          <>
                            <li>
                              <span className="listTitle">Country:</span>{" "}
                              {account?.personalAccountInfo?.country?.name}
                            </li>
                            <li>
                              <span className="listTitle">City:</span>{" "}
                              {account?.personalAccountInfo?.city?.name}
                            </li>
                          </>
                        ) : (
                          ""
                        )}
                      </ul>
                    </Box>
                    {account?.organization ? (
                      <Box sx={{ width: { xs: "100%", md: "50%" } }}>
                        <h3 style={{ fontSize: "20px", margin: "0 0 15px" }}>
                          Organization details:
                        </h3>
                        <ul className="confirmList">
                          <li>
                            <span className="listTitle">Name:</span>{" "}
                            {account?.organization?.name}
                          </li>
                          <li>
                            <span className="listTitle">Phone Number:</span>{" "}
                            {account?.organization?.branch?.contactNumber}
                          </li>
                          <li>
                            <span className="listTitle">Branch Country:</span>{" "}
                            {account?.organization?.branch?.country?.name}
                          </li>
                          <li>
                            <span className="listTitle">Branch City:</span>{" "}
                            {account?.organization?.branch?.city?.name}
                          </li>
                          <li>
                            <span className="listTitle">Address:</span>{" "}
                            {account?.organization?.branch?.address}
                          </li>
                        </ul>
                      </Box>
                    ) : (
                      ""
                    )}
                  </Box>
                </div>
              </div>
            </CustomTabPanel>
            <CustomTabPanel value={value} index={1}>
              {account?.teams?.length ? (
                <div className="teamsCont">
                  {account?.teams?.map((team) => (
                    <div className="teamBox">
                      <Box
                        sx={{ cursor: "pointer" }}
                        onClick={() => navigate(`/teams/${team?.id}`)}
                      >
                        <ul className="confirmList">
                          <li>
                            <span className="listTitle">Team Name:</span>{" "}
                            {team?.name}
                          </li>
                          <li>
                            <span className="listTitle">Competition:</span>{" "}
                            {team?.onlineParticipant
                              ? team?.offlineParticipant
                                ? "Both (online & offline)"
                                : "Online Competition"
                              : "Offline Competition"}
                          </li>
                          <li>
                            <span className="listTitle">Category:</span>{" "}
                            {team?.category?.name}
                          </li>
                          <li>
                            <span className="listTitle">Age Group:</span>{" "}
                            {team?.ageGroup?.name}
                          </li>
                          <li>
                            <span className="listTitle">Price:</span>{" "}
                            {(team?.price?.amountCents / 100).toFixed(2)}{" "}
                            {team?.price?.currency?.code}
                          </li>
                          <li>
                            <span className="listTitle">Coach Name:</span>{" "}
                            {team?.coach?.name}
                          </li>
                          <li>
                            <span className="listTitle">
                              Coach Phone Number:
                            </span>{" "}
                            {team?.coach?.contactNumber}
                          </li>
                          <li>
                            <span className="listTitle">Team Members:</span>
                          </li>
                        </ul>
                        <TableContainer
                          component={Paper}
                          sx={{
                            marginTop: "10px",
                          }}
                        >
                          <Table aria-label="customized table">
                            <TableHead>
                              <TableRow>
                                <StyledTableCell align="center">
                                  name
                                </StyledTableCell>
                                <StyledTableCell align="center">
                                  Birthday
                                </StyledTableCell>
                                <StyledTableCell align="center">
                                  Age
                                </StyledTableCell>
                                {/* <StyledTableCell align="center">
                                  National ID
                                </StyledTableCell> */}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {team?.members?.map((stu) => (
                                <StyledTableRow key={stu.name}>
                                  <StyledTableCell align="center">
                                    {stu.name}
                                  </StyledTableCell>
                                  <StyledTableCell align="center">
                                    {stu?.birthday?.split("T")?.at(0)}
                                  </StyledTableCell>
                                  <StyledTableCell align="center">
                                    {stu?.age}
                                  </StyledTableCell>
                                  {/* <StyledTableCell align="center">
                                    {stu?.nationalId}
                                  </StyledTableCell> */}
                                </StyledTableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </div>
                  ))}
                </div>
              ) : (
                <h3 style={{ textAlign: "center", margin: "50px" }}>
                  No Teams Added Yet!!
                </h3>
              )}
            </CustomTabPanel>
            <CustomTabPanel value={value} index={2}>
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <PrimaryButton
                  classBtn={"center addButton"}
                  text={"Manual Payment"}
                  style={{
                    textTransform: "capitalize",
                    borderRadius: "5px",
                    color: "white",
                    backgroundColor: "var(--primary)",
                    height: "40px",
                    padding: "0 30px",
                    minWidth: "100px",
                    textWrap: "noWrap",
                  }}
                  click={handleOpenPaymentModal}
                />
                <PrimaryButton
                  classBtn={"center addButton"}
                  text={"Apply Discount"}
                  style={{
                    textTransform: "capitalize",
                    borderRadius: "5px",
                    color: "white",
                    backgroundColor: "var(--primary)",
                    height: "40px",
                    padding: "0 30px",
                    minWidth: "100px",
                    textWrap: "noWrap",
                  }}
                  click={handleOpenDiscountModal}
                />
              </Box>

              <FormModal
                CustomStyle={"smallExtra"}
                Open={openPaymentModal}
                HandleClose={handleClosePaymentModal}
                Form={
                  <ConfirmPayment
                    handleClosePaymentModal={handleClosePaymentModal}
                    account={account}
                  />
                }
                Title={"Manual Payment"}
              />

              <FormModal
                CustomStyle={"smallExtra"}
                Open={openDiscountModal}
                HandleClose={handleCloseDiscountModal}
                Form={
                  <DiscountModal
                    handleCloseDiscountModal={handleCloseDiscountModal}
                    account={account}
                  />
                }
                Title={"Apply Discount"}
              />
              <div className="greyBox" style={{ alignItems: "flex-start" }}>
                <Box sx={{ width: { xs: "100%", md: "50%" } }}>
                  <h3 style={{ fontSize: "20px" }}>Payment Details:</h3>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                      mb: 2,
                      mt: 2,
                    }}
                  >
                    <ul className="confirmList">
                      <li>
                        <span className="listTitle">Paid:</span>{" "}
                        {account?.paymentData?.details?.paid
                          ? "Yes"
                          : "Not Yet"}
                      </li>
                      <li>
                        <span className="listTitle">Can Pay:</span>{" "}
                        {account?.paymentData?.details?.paid
                          ? "Yes"
                          : "Not Yet"}
                      </li>
                      <li>
                        <span className="listTitle">Payment Status:</span>{" "}
                        {account?.paymentData?.details?.paymentStatus ?? "-"}
                      </li>
                      <li>
                        <span className="listTitle">Amount To Pay:</span>{" "}
                        {account?.paymentData?.details?.amountToPay?.amountCents
                          ? ` ${(
                              account?.paymentData?.details?.amountToPay
                                ?.amountCents / 100
                            ).toFixed(2)} ${
                              account?.paymentData?.details?.amountToPay
                                ?.currency?.code
                            }`
                          : "-"}
                      </li>
                      <li>
                        <span className="listTitle">Amount To Refund:</span>{" "}
                        {account?.paymentData?.details?.amountToRefund
                          ?.amountCents
                          ? ` ${(
                              account?.paymentData?.details?.amountToRefund
                                ?.amountCents / 100
                            ).toFixed(2)} ${
                              account?.paymentData?.details?.amountToRefund
                                ?.currency?.code
                            }`
                          : "-"}
                      </li>
                    </ul>
                  </Box>
                </Box>
                <Box sx={{ width: { xs: "100%", md: "50%" } }}>
                  <h3 style={{ fontSize: "20px", margin: "0 0 15px" }}>
                    Account Price:
                  </h3>
                  <ul className="confirmList">
                    <li>
                      <span className="listTitle">Total Price:</span>{" "}
                      {account?.paymentData?.accountPrice?.totalPriceAmountCents
                        ? `${(
                            account?.paymentData?.accountPrice
                              ?.totalPriceAmountCents / 100
                          ).toFixed(2)} ${
                            account?.paymentData?.accountPrice?.currency?.code
                          }`
                        : "-"}
                    </li>
                    <li>
                      <span className="listTitle">Discount Percentage:</span>{" "}
                      {account?.paymentData?.accountPrice?.discount?.percentage
                        ? `${account?.paymentData?.accountPrice?.discount?.percentage}%`
                        : "-"}
                    </li>
                    <li>
                      <span className="listTitle">Discount Amount:</span>{" "}
                      {account?.paymentData?.accountPrice?.discount?.amountCents
                        ? ` ${(
                            account?.paymentData?.accountPrice?.discount
                              ?.amountCents / 100
                          ).toFixed(2)} ${
                            account?.paymentData?.accountPrice?.currency?.code
                          }`
                        : "-"}
                    </li>
                    <li>
                      <span className="listTitle">
                        Total Price After Discount:
                      </span>{" "}
                      {account?.paymentData?.accountPrice?.discount
                        ?.totalPriceAmountCentsAfterDiscount
                        ? ` ${(
                            account?.paymentData?.accountPrice?.discount
                              ?.totalPriceAmountCentsAfterDiscount / 100
                          ).toFixed(2)} ${
                            account?.paymentData?.accountPrice?.currency?.code
                          }`
                        : "-"}
                    </li>
                    <li>
                      <span className="listTitle">
                        Total Transaction Net Amount:
                      </span>{" "}
                      {account?.paymentData?.totalTransactionsNetAmount
                        ?.amountCents
                        ? ` ${(
                            account?.paymentData?.totalTransactionsNetAmount
                              ?.amountCents / 100
                          ).toFixed(2)} ${
                            account?.paymentData?.totalTransactionsNetAmount
                              ?.currency?.code
                          }`
                        : "-"}
                    </li>
                  </ul>
                </Box>
                <Box sx={{ width: "100%" }}>
                  <h3 style={{ fontSize: "20px", margin: "15px 0 10px" }}>
                    Transactions History:{" "}
                    {!account?.paymentData?.paymentTransactions?.length ? (
                      <span
                        style={{
                          fontSize: "15px",
                          fontWeight: "400",
                          marginLeft: "5px",
                        }}
                      >
                        No Transactions Yet..
                      </span>
                    ) : (
                      ""
                    )}
                  </h3>
                  <TableContainer
                    component={Paper}
                    sx={{
                      marginTop: "10px",
                      width: { xs: "100%" },
                    }}
                  >
                    <Table aria-label="customized table">
                      <TableHead>
                        <TableRow>
                          <StyledTableCell align="center">ID</StyledTableCell>
                          <StyledTableCell align="center">Code</StyledTableCell>
                          <StyledTableCell align="center">
                            Method
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            Date/Time
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            Net Amount
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            Tax Amount
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            Total
                          </StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {account?.paymentData?.paymentTransactions?.length
                          ? account?.paymentData?.paymentTransactions?.map(
                              (transaction) => (
                                <StyledTableRow key={transaction.id}>
                                  <StyledTableCell align="center">
                                    {transaction.id ?? "-"}
                                  </StyledTableCell>
                                  <StyledTableCell align="center">
                                    {transaction.code ?? "-"}
                                  </StyledTableCell>
                                  <StyledTableCell align="center">
                                    {transaction.paymentMethod ?? "-"}
                                  </StyledTableCell>
                                  <StyledTableCell align="center">
                                    {transaction.createdAt
                                      ? new Date(
                                          transaction.createdAt
                                        )?.toLocaleTimeString()
                                      : ""}
                                  </StyledTableCell>
                                  <StyledTableCell align="center">
                                    {(
                                      transaction?.netAmountCents / 100
                                    ).toFixed(2)}{" "}
                                    {transaction?.currency?.code}
                                  </StyledTableCell>
                                  <StyledTableCell align="center">
                                    {(
                                      transaction?.taxAmountCents / 100
                                    ).toFixed(2)}{" "}
                                    {transaction?.currency?.code}
                                  </StyledTableCell>
                                  <StyledTableCell align="center">
                                    {(
                                      transaction?.totalAmountCents / 100
                                    ).toFixed(2)}{" "}
                                    {transaction?.currency?.code}
                                  </StyledTableCell>
                                </StyledTableRow>
                              )
                            )
                          : ""}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </div>
            </CustomTabPanel>
          </Box>
        </div>
      ) : (
        <div className="loadingBox">
          <BarLoader color="var(--primary)" />
        </div>
      )}
    </div>
  );
}

export default AccountsDetails;
